import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import {
  buildSendMessageFormData,
  canSendMessage,
  createAssistantPlaceholder,
  createOptimisticUserMessage,
  doSendMessage,
  luHandleAppiError,
  U3204_ERROR_CODE_GROUP_QUOTA_EXCEEDED,
  U3204_ERROR_CODE_USER_QUOTA_EXCEEDED,
  validateAttachments,
} from '../U3204Service';

interface UseU3204SendMessageHandlersOptions {
  onAfterQuotaError?: () => void | Promise<void>;
}

export function useU3204SendMessageHandlers(options?: UseU3204SendMessageHandlersOptions) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(NS);
  const toast = useToast();
  const {
    initLU,
    activeConversationId,
    messages,
    setMessages,
    setStreamState,
    allowedAttachmentMimeTypes,
    modelCapabilities,
  } = useU3204Context();

  const sendMessage = useCallback(
    async (content: string, files: File[], conversationIdOverride?: string) => {
      const conversationId = conversationIdOverride ?? activeConversationId;
      if (!conversationId) {
        toast.error(t('errNoConversation', { ns: NS }));
        return false;
      }

      if (!canSendMessage(content, files)) {
        toast.error(t('errEmptyMessage', { ns: NS }));
        return false;
      }

      const validationError = validateAttachments(files, initLU, {
        allowedMimeTypes: allowedAttachmentMimeTypes,
        capabilities: modelCapabilities,
      });
      if (validationError) {
        const key =
          validationError.code === 'too_many'
            ? 'errTooManyAttachments'
            : validationError.code === 'unsupported_mime'
              ? 'errUnsupportedMime'
              : validationError.code === 'image_not_supported'
                ? 'errImageNotSupported'
                : validationError.code === 'pdf_not_supported'
                  ? 'errPdfNotSupported'
                  : 'errFileTooLarge';
        toast.error(t(key, { ns: NS }));
        return false;
      }

      const tempUserId = `temp-user-${Date.now()}`;
      const optimisticUser = createOptimisticUserMessage(content, files, tempUserId);
      const assistantPlaceholder = createAssistantPlaceholder();

      setMessages((prev) => [...prev, optimisticUser, assistantPlaceholder]);
      setStreamState('streaming');

      try {
        const formData = buildSendMessageFormData(content, files);
        const response = await doSendMessage(axios, conversationId, formData);
        const userMessageId: string | undefined = response.data?.result?.userMessageId;

        if (userMessageId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === tempUserId ? { ...m, id: userMessageId, isOptimistic: false } : m)),
          );
        }

        return true;
      } catch (error: unknown) {
        setStreamState('error');
        setMessages((prev) => prev.filter((m) => m.id !== tempUserId && m.id !== assistantPlaceholder.id));

        const apiErr = error as { errorCode?: string };
        if (
          apiErr?.errorCode === U3204_ERROR_CODE_USER_QUOTA_EXCEEDED ||
          apiErr?.errorCode === U3204_ERROR_CODE_GROUP_QUOTA_EXCEEDED
        ) {
          await options?.onAfterQuotaError?.();
        }

        const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError) as U2000ApiErrorResult;
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
          Object.values(errorResult.otherErrors).forEach((msg) => toast.error(String(msg)));
        }
        return false;
      }
    },
    [activeConversationId, allowedAttachmentMimeTypes, axios, initLU, modelCapabilities, options, setMessages, setStreamState, t, toast],
  );

  const isSending = streamStateFromMessages(messages);

  return { sendMessage, isSending };
}

function streamStateFromMessages(messages: { generationStatus?: string; role: string }[]): boolean {
  return messages.some((m) => m.role === 'assistant' && m.generationStatus === 'pending');
}
